import React, { useState } from "react";
import { ArrowUpRight, Check, Sparkles } from "lucide-react";

/**
 * Live preview of all 5 theme directions.
 * Each card renders the same "hero + button + typography sample"
 * inside a scoped container with that theme's palette + fonts applied.
 */

const themes = [
  {
    id: "A",
    name: "Editorial Noir",
    tag: "Current direction",
    description: "Bold magazine energy. Oversized italic serif display, warm cream paper, molten orange accent. Feels human, creative, confident.",
    palette: {
      bg: "#F4EEE3",
      ink: "#0B0B0C",
      accent: "#FF5B2E",
      muted: "#6B6962",
      rule: "rgba(11,11,12,0.15)",
    },
    fonts: {
      display: "'Instrument Serif', Georgia, serif",
      body: "'Inter', sans-serif",
      mono: "'JetBrains Mono', monospace",
    },
    fontLabel: "Instrument Serif · Inter",
  },
  {
    id: "B",
    name: "Midnight Electric",
    tag: "Tech-forward · Cyber energy",
    description: "Dark charcoal canvas, techy grotesque display, electric lime that cuts through. Feels AI-native and engineered.",
    palette: {
      bg: "#0D0E12",
      ink: "#EFEFEC",
      accent: "#CCFF00",
      muted: "#8A8A8F",
      rule: "rgba(239,239,236,0.14)",
    },
    fonts: {
      display: "'Bricolage Grotesque', sans-serif",
      body: "'Inter', sans-serif",
      mono: "'JetBrains Mono', monospace",
    },
    fontLabel: "Bricolage Grotesque · JetBrains Mono",
  },
  {
    id: "C",
    name: "Royal Heritage",
    tag: "Executive · Trusted",
    description: "Ivory paper, deep midnight navy ink, burnished gold accent. Reads like a board-room keynote. Quiet authority.",
    palette: {
      bg: "#F6F1E7",
      ink: "#0F1E3D",
      accent: "#C9A14B",
      muted: "#5A5F6E",
      rule: "rgba(15,30,61,0.18)",
    },
    fonts: {
      display: "'Fraunces', Georgia, serif",
      body: "'Inter', sans-serif",
      mono: "'JetBrains Mono', monospace",
    },
    fontLabel: "Fraunces · Inter",
  },
  {
    id: "D",
    name: "Terracotta & Bone",
    tag: "Warm · Distinctive",
    description: "Bone paper, espresso ink, terracotta accent with a sage whisper. Warm, confident, unmistakable.",
    palette: {
      bg: "#F0EAE0",
      ink: "#1F1814",
      accent: "#B9462D",
      muted: "#8A9B76",
      rule: "rgba(31,24,20,0.18)",
    },
    fonts: {
      display: "'Playfair Display', Georgia, serif",
      body: "'Figtree', sans-serif",
      mono: "'JetBrains Mono', monospace",
    },
    fontLabel: "Playfair Display · Figtree",
  },
  {
    id: "E",
    name: "Mono Minimal",
    tag: "Swiss · High-signal",
    description: "Pure paper, jet ink, one blood-red accent. Clinical typography. Lets the content shout.",
    palette: {
      bg: "#FAFAFA",
      ink: "#111111",
      accent: "#D62828",
      muted: "#6B6B6B",
      rule: "rgba(17,17,17,0.14)",
    },
    fonts: {
      display: "'Inter', sans-serif",
      body: "'Inter', sans-serif",
      mono: "'IBM Plex Mono', monospace",
    },
    fontLabel: "Inter (tight) · IBM Plex Mono",
  },
];

function ThemeCard({ t, selected, onSelect }) {
  const { palette, fonts } = t;
  const isMidnight = t.id === "B";
  const isMinimal = t.id === "E";

  return (
    <div
      onClick={onSelect}
      className={`cursor-pointer rounded-sm border transition-all duration-500 ${
        selected
          ? "border-[var(--molten)] shadow-[0_18px_60px_-20px_rgba(255,91,46,0.45)]"
          : "border-[var(--ink)]/15 hover:border-[var(--ink)]/40"
      }`}
      style={{ background: palette.bg, color: palette.ink }}
    >
      {/* Header band */}
      <div
        className="flex items-center justify-between px-6 py-4"
        style={{
          borderBottom: `1px solid ${palette.rule}`,
          fontFamily: fonts.mono,
          fontSize: "0.68rem",
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: palette.muted,
        }}
      >
        <span>[ {t.id} · {t.tag} ]</span>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full border" style={{ background: palette.bg, borderColor: palette.rule }} />
          <span className="w-3 h-3 rounded-full" style={{ background: palette.ink }} />
          <span className="w-3 h-3 rounded-full" style={{ background: palette.accent }} />
        </div>
      </div>

      {/* Preview canvas */}
      <div className="p-8 md:p-10 min-h-[420px] flex flex-col justify-between">
        <div>
          <div
            style={{
              fontFamily: fonts.mono,
              fontSize: "0.7rem",
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: palette.muted,
            }}
          >
            Dr. Drijesh Ppatel · Engineering Leader
          </div>

          <h2
            className="mt-5"
            style={{
              fontFamily: fonts.display,
              fontSize: isMinimal ? "2.4rem" : "3.3rem",
              lineHeight: isMinimal ? "1.02" : "0.96",
              letterSpacing: isMinimal ? "-0.035em" : "-0.01em",
              fontWeight: isMinimal ? 700 : 400,
            }}
          >
            Building{" "}
            <span
              style={{
                fontStyle: isMinimal ? "normal" : "italic",
                color: palette.accent,
                fontWeight: isMinimal ? 700 : 400,
              }}
            >
              AI-driven
            </span>{" "}
            systems.
          </h2>

          <p
            className="mt-5"
            style={{
              fontFamily: fonts.body,
              color: isMidnight ? "rgba(239,239,236,0.72)" : palette.muted,
              lineHeight: 1.55,
              fontSize: "0.92rem",
              maxWidth: "34ch",
            }}
          >
            Helping organisations adopt AI, build resilient systems, and ship products that move real outcomes.
          </p>
        </div>

        <div className="flex items-center justify-between mt-8 flex-wrap gap-3">
          <div className="flex flex-wrap gap-2">
            <button
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium"
              style={{
                background: palette.ink,
                color: palette.bg,
                fontFamily: fonts.body,
              }}
            >
              Work with me <ArrowUpRight size={13} />
            </button>
            <button
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium"
              style={{
                background: palette.accent,
                color: isMinimal || t.id === "C" ? "#fff" : palette.ink,
                fontFamily: fonts.body,
              }}
            >
              Speak <Sparkles size={13} />
            </button>
          </div>
          <div
            style={{
              fontFamily: fonts.mono,
              fontSize: "0.66rem",
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: palette.muted,
            }}
          >
            {t.fontLabel}
          </div>
        </div>
      </div>

      {/* Swatch strip */}
      <div className="grid grid-cols-5" style={{ borderTop: `1px solid ${palette.rule}` }}>
        {[palette.bg, palette.ink, palette.accent, palette.muted, palette.bg === "#F4EEE3" ? "#EFE8DB" : palette.rule].map((c, i) => (
          <div key={i} className="h-10" style={{ background: c }} />
        ))}
      </div>

      {/* Meta */}
      <div className="px-6 py-4" style={{ borderTop: `1px solid ${palette.rule}`, fontFamily: fonts.body }}>
        <div className="flex items-center justify-between">
          <div
            style={{
              fontFamily: fonts.display,
              fontSize: "1.35rem",
              lineHeight: 1.1,
              fontStyle: isMinimal ? "normal" : "normal",
              fontWeight: isMinimal ? 600 : 400,
              letterSpacing: isMinimal ? "-0.02em" : "0",
            }}
          >
            {t.name}
          </div>
          <div
            className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full transition-colors`}
            style={{
              background: selected ? palette.accent : "transparent",
              color: selected ? (isMinimal || t.id === "C" ? "#fff" : palette.ink) : palette.muted,
              border: `1px solid ${selected ? palette.accent : palette.rule}`,
              fontFamily: fonts.mono,
              fontSize: "0.66rem",
              letterSpacing: "0.14em",
              textTransform: "uppercase",
            }}
          >
            {selected ? <><Check size={12} /> Picked</> : "Select"}
          </div>
        </div>
        <p
          className="mt-3"
          style={{
            color: isMidnight ? "rgba(239,239,236,0.65)" : palette.muted,
            fontSize: "0.82rem",
            lineHeight: 1.55,
          }}
        >
          {t.description}
        </p>
      </div>
    </div>
  );
}

export default function Themes() {
  const [selected, setSelected] = useState("A");

  return (
    <main className="bg-[var(--cream)] text-[var(--ink)]">
      <section className="pt-16 md:pt-24 pb-12 grain">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="tag-mono mb-6">[ Theme preview ]</div>
          <h1 className="headline-xl max-w-5xl">
            Pick the <span className="italic-serif text-[var(--molten)]">look &amp; feel.</span>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-[var(--ink)]/75 max-w-3xl leading-relaxed">
            Five directions rendered live with real typography and real palettes. Click a card to mark it as your pick, then tell me the letter (A–E) and I'll apply it across every page.
          </p>
        </div>
      </section>

      <section className="pb-24 grain">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {themes.map((t) => (
              <ThemeCard
                key={t.id}
                t={t}
                selected={selected === t.id}
                onSelect={() => setSelected(t.id)}
              />
            ))}
          </div>

          <div className="mt-14 p-8 md:p-10 section-ink grain flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div className="tag-mono mb-3">[ Your pick ]</div>
              <div className="font-serif text-3xl md:text-4xl text-[var(--cream)]">
                Theme <span className="italic-serif text-[var(--molten)]">{selected}</span> —{" "}
                {themes.find((t) => t.id === selected)?.name}
              </div>
              <p className="mt-3 text-[var(--cream)]/70 max-w-2xl leading-relaxed">
                Reply with just the letter (e.g. "Go with C") and I'll re-theme the entire portfolio to match, including fonts, palette, buttons, and accents.
              </p>
            </div>
            <div className="font-mono text-xs uppercase tracking-[0.14em] text-[var(--cream)]/60 md:text-right shrink-0">
              5 directions<br/>Click any card to preview pick
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
